import React from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { createSelector } from 'reselect'

import { makeStyles, Typography, Grid } from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import Skeleton from '@material-ui/lab/Skeleton'

import ButtonBuyOnline from '~/components/ButtonBuyOnline'
import useEcommerce from '~/components/Ecommerce/useEcommerce'
import { useLiterals } from '~/components/Literals'
import Tooltip from '~/components/Tooltip'

import useViewportSize from '~/components/useViewportSize'
import { selectStores } from '~/selectors'
import { TYPES } from '~/actions'
import useIsMount from '../useIsMount'

// styles (mui)
const useStyles = makeStyles((theme) => ({
  header: {
    position: 'relative',
    height: 44,
    background: '#E7E9EA',
    color: theme.palette.secondary.contrastText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '28px 8px'
  },
  ButtonBuyOnline: {
    minWidth: 160
  },
  selected: {
    display: 'flex',
    alignItems: 'center',
    '& > svg': {
      marginLeft: 4,
      cursor: 'pointer'
    }
  }
}))

function MakeMessage({
  view,
  productsLoading,
  storesLoading,
  stores,
  searchProductResults,
  config: { multiProduct, listingPlaceholder },
  searchLoading,
  getLiteral
}) {
  const isMounted = useIsMount()

  if (!isMounted || productsLoading || storesLoading)
    return <Skeleton variant="text" width="100%" />

  if (view === TYPES.VIEW_PRODUCTS) {
    if (productsLoading) return getLiteral('1994')

    if (multiProduct) return getLiteral('1181')

    return listingPlaceholder ? listingPlaceholder : getLiteral('1357')
  }

  if (view === TYPES.VIEW_STORES) {
    if (storesLoading) return getLiteral('1998')

    if (stores && stores.length) {
      return stores.length > 1
        ? getLiteral('2000', [stores.length])
        : getLiteral('1999', [stores.length])
    }

    return null
  }

  if (view === TYPES.VIEW_SEARCH_PRODUCTS) {
    if (productsLoading || searchLoading) return getLiteral('1880')

    if (searchProductResults && searchProductResults.length) {
      if (multiProduct) return getLiteral('1181')

      return listingPlaceholder ? listingPlaceholder : getLiteral('1357')
    }
  }

  return ''
}

const selector = createSelector(
  (state) => state.app,
  (...args) => args
)

function SubHeader({ config, position }) {
  const [app] = useSelector(selector, shallowEqual)
  const { stores } = useSelector(selectStores, shallowEqual)

  const classes = useStyles()
  const { getLiteral } = useLiterals()

  const { view } = app
  const url = window.location.toString().includes('/produto/todos')

  const viewport = useViewportSize()
  const isMobile = viewport.width <= 330

  const { openEcommerce, setOpenEcommerce, dataEcommerce, loadingEcommerce, setApiEcommerce } =
    useEcommerce()

  if (
    view === TYPES.VIEW_STORE_DETAILS ||
    view === TYPES.VIEW_PRODUCTS ||
    view === TYPES.VIEW_SEARCH_PRODUCTS
  )
    return null

  return (
    <Grid className={classes.header}>
      {!isMobile ? (
        <Typography
          variant="subtitle2"
          color="inherit"
          style={{ flex: 1, paddingRight: 8, display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <MakeMessage {...app} config={config} getLiteral={getLiteral} stores={stores} />{' '}
          {stores && stores.length >= 10 && (
            <Tooltip title={getLiteral('2549')}>
              <InfoOutlinedIcon fontSize="small" />
            </Tooltip>
          )}
        </Typography>
      ) : null}

      {view === 'VIEW_STORES' && !config.generic && !url && app.currentProduct?.gtin !== 'all' && (
        <Grid className={classes.ButtonBuyOnline}>
          <ButtonBuyOnline
            product={app.currentProduct}
            disabled={app.storesLoading}
            position={position}
            openEcommerce={openEcommerce}
            setOpenEcommerce={setOpenEcommerce}
            setApiEcommerce={setApiEcommerce}
            dataEcommerce={dataEcommerce}
            loadingEcommerce={loadingEcommerce}
          />
        </Grid>
      )}
    </Grid>
  )
}

export default SubHeader
